import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { Popover, Tab, TabList, TabPanel, Tabs, Text } from '@launchpad-ui/components';

import { useHoverPopover } from '../../internal/components/AgentInsights/useHoverPopover';

import { ConversationArtifactSections } from './components/artifacts/ConversationArtifactSections';
import { ConversationArtifactsPane } from './components/artifacts/ConversationArtifactsPane';
import { useConversationArtifactSections } from './components/artifacts/useConversationArtifactSections';

import styles from './VegaConversationTabs.module.css';

type ConversationTab = 'messages' | 'artifacts';

interface Props {
  projectId: string;
  projectKey: string;
  conversationId: string;
  // The conversation column (AgentInsights) built by the host; slotted into the Messages tab.
  messages: React.ReactNode;
  /**
   * When true (default), the selected tab is mirrored to a `?tab=` search param so it's
   * deep-linkable on the full-page conversation view. The side panel passes false — the host
   * URL already carries `showVega` / `AgentConversationId`, and syncing `tab` there would
   * collide with unrelated page state.
   */
  syncTabToSearchParams?: boolean;
}

function isConversationTab(key: string | number): key is ConversationTab {
  return key === 'messages' || key === 'artifacts';
}

/**
 * Tabbed chrome for a Vega conversation: a Messages tab (the chat) and an Artifacts tab that
 * swaps the pane to the conversation's grouped artifact list when clicked and opens a
 * quick-access dropdown of the same list on hover/focus.
 *
 * Uses LaunchPad Tabs (not observability-ui) so Vega aligns with the design system without
 * reverse-depending on observability. Hover popover is local: handlers + Popover on the
 * Artifacts tab — no LaunchPad API changes required.
 *
 * Used by the full-page conversation view (`VegaConversationPage`) and the slide-out
 * `AgentInsightsPanel`. Gated upstream by `vega-artifact-graph` and a saved conversation id
 * (`new` can't have artifacts). Canvas is intentionally not shipped yet.
 */
export const VegaConversationTabs: React.FC<Props> = ({
  projectId,
  projectKey,
  conversationId,
  messages,
  syncTabToSearchParams = true,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab: ConversationTab =
    syncTabToSearchParams && searchParams.get('tab') === 'artifacts' ? 'artifacts' : 'messages';
  const [selectedTab, setSelectedTab] = useState<ConversationTab>(urlTab);

  const { sections, hasAny, totalCount, loading, error } = useConversationArtifactSections({
    projectId,
    conversationId,
  });

  const artifactsTabRef = useRef<HTMLDivElement | null>(null);
  const { isOpen, close, hoverHandlers } = useHoverPopover();

  const handleSetTab = useCallback(
    (key: string | number) => {
      if (!isConversationTab(key)) {
        return;
      }
      // Once a tab is selected the redundant hover dropdown should get out of the way.
      close();
      setSelectedTab(key);
      if (!syncTabToSearchParams) {
        return;
      }
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', key);
          return next;
        },
        { replace: true },
      );
    },
    [close, setSearchParams, syncTabToSearchParams],
  );

  // Keep local state in sync when the URL changes out from under us (back/forward, deep link).
  useEffect(() => {
    if (!syncTabToSearchParams) {
      return;
    }
    if (urlTab !== selectedTab) {
      setSelectedTab(urlTab);
    }
    // Only react to URL changes; handleSetTab already updates both in the same tick.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [urlTab, syncTabToSearchParams]);

  return (
    <div className={styles.root}>
      <Tabs
        selectedKey={selectedTab}
        onSelectionChange={handleSetTab}
        className={styles.tabs}
        aria-label={msg('vega.conversationTabs.tabsLabel')}
      >
        <TabList className={styles.tabList}>
          <Tab id="messages" className={styles.tab}>
            {msg('vega.conversationTabs.messages')}
          </Tab>
          {/* Hover/focus handlers on the tab so hovering opens the dropdown while clicking
              still switches the pane. Ref anchors the Popover (prefer over getElementById —
              react-aria selection keys are not necessarily DOM ids). */}
          <Tab
            id="artifacts"
            ref={artifactsTabRef}
            className={styles.tab}
            aria-haspopup="menu"
            aria-expanded={isOpen}
            {...hoverHandlers}
          >
            {msg('vega.conversationTabs.artifacts')}
            {totalCount > 0 ? <span className={styles.badge}>{totalCount}</span> : null}
          </Tab>
        </TabList>

        {/* Keep Messages mounted while hidden: conversation data lives in context, but the
            in-flight streaming animation, scroll position, and composer draft do not. */}
        <TabPanel id="messages" shouldForceMount className={styles.panel}>
          {messages}
        </TabPanel>

        <TabPanel id="artifacts" className={styles.artifactsPanel}>
          <ConversationArtifactsPane
            projectId={projectId}
            projectKey={projectKey}
            sections={sections}
            hasAny={hasAny}
            loading={loading}
            error={error}
          />
        </TabPanel>
      </Tabs>

      <Popover
        triggerRef={artifactsTabRef}
        isOpen={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            close();
          }
        }}
        placement="bottom start"
        // Non-modal so opening on hover doesn't grab focus or add a dismiss underlay — those
        // fire onOpenChange(false) right after the hover opens it, causing an open/close flicker.
        isNonModal
      >
        <div className={styles.dropdown} {...hoverHandlers}>
          {loading && !hasAny ? (
            <Text size="small" className={styles.dropdownMessage}>
              <Message id="vega.status.loading" />
            </Text>
          ) : !hasAny ? (
            <Text size="small" className={styles.dropdownMessage}>
              <Message id="vega.conversationTabs.noArtifactsYet" />
            </Text>
          ) : (
            <ConversationArtifactSections
              projectId={projectId}
              projectKey={projectKey}
              sections={sections}
              density="compact"
            />
          )}
        </div>
      </Popover>
    </div>
  );
};
